<script setup lang="ts">
import { defineEmits, ref, watch, reactive } from 'vue';
import {PlusIcon} from '@heroicons/vue/24/outline';
import {useVuelidate} from "@vuelidate/core";
import {helpers, requiredIf} from "@vuelidate/validators";
import CountrySelect from '@/components/forms/CountrySelect.vue'
import PassengerCard from '@/components/passengers/PassengerCard.vue'
import BaseButton from "@/components/ui/buttons/BaseButton.vue";
import { TicketIcon } from '@heroicons/vue/24/solid';
import BaseInput from '@/components/ui/inputs/BaseInput.vue';

const emits = defineEmits(['update:passengers']);
const passengers = ref<any[]>([]);
const defaultValue = {
  firstName: '',
  lastName: '',
  email: '',
  nationalityCountryCode: 'FR',
  ticketNumber: null as null | string,
}
const form = ref({ ...defaultValue })

const addPassenger = async () => {
  await v$.value.$validate()
  if (v$.value.$errors.length === 0) {
    v$.value.$reset()
    passengers.value.push({...form.value})
    console.log(".......form.value", form.value)
    console.log(".......defaultValue", defaultValue)
    resetForm();
    console.log("ok reset......")
  }
};

function resetForm() {
  Object.assign(form.value, { ...defaultValue });
}

const onRemovePassenger = (index) => {
  if (passengers.value.length > 0) {
    passengers.value.splice(index, 1);
  }
}

const setCountry = (countryCode) => {
  form.value.nationalityCountryCode = countryCode
}

watch(passengers.value, passengers => {
  emits('update:passengers', passengers)
})

const eTicketRegex = helpers.regex(/^[0-9]{3}(-)?[0-9]{10}$/)

const rules = {
  firstName: {required: helpers.withMessage('Le prénom est manquant', requiredIf(() => passengers.value.length === 0))},
  lastName: {required: helpers.withMessage('Le nom de famille est manquant', requiredIf(() => passengers.value.length === 0))},
  nationalityCountryCode: {required: helpers.withMessage('Ce champs ne peut être vide', requiredIf(() => passengers.value.length === 0))},
  email: {
    // Optional email validation
    email: helpers.withMessage('L\'email n\'est pas valide', (value: string) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
  },
  ticketNumber: {
    // Optional alphanumeric validation
    alphaNumeric: helpers.withMessage('La valeur doit être un numéro de billet (exemple: 057-9389633557)', eTicketRegex)
  }
}

const v$ = useVuelidate(rules, form)
</script>
<template>
  <div class="grid grid-cols-1 pt-8 text-left gap-x-8 gap-y-8 md:grid-cols-3">
    <div class="px-4 text-left sm:px-0">
      <h2 class="text-base font-semibold leading-7 text-gray-900">Ajouter des passagers </h2>
      <p class="mt-1 text-sm leading-6 text-gray-600">Passager(s) présent(s) sur la réservation.</p>
    </div>
    <form class="shadow ring-1 ring-gray-900/5 sm:rounded-md md:col-span-2 bg-zinc-50">
      <div class="px-4 py-6 sm:p-8">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <BaseInput v-model="form.firstName" :value="form.firstName" label="Prénom" placeholder="Prénom" />
            <p v-if="v$.$errors.find(err => err.$property === 'firstName')"
               class="mt-1 text-sm text-left text-red-500">{{
                v$.$errors.find(err => err.$property === 'firstName')?.$message
              }}</p>
          </div>

          <div>
            <BaseInput v-model="form.lastName" :value="form.lastName" label="Nom" placeholder="Nom de famille" />
            <p v-if="v$.$errors.find(err => err.$property === 'lastName')"
               class="mt-1 text-sm text-left text-red-500">{{
                v$.$errors.find(err => err.$property === 'lastName')?.$message
              }}</p>
          </div>
          <div>
            <BaseInput v-model="form.email" :value="form.email" label="Email" placeholder="Adresse mail" />
            <p v-if="v$.$errors.find(err => err.$property === 'email')"
               class="mt-1 text-sm text-left text-red-500">{{
                v$.$errors.find(err => err.$property === 'email')?.$message
              }}</p>
          </div>
          <div class="relative mt-2">
            <label for="flightNumber"
                   class="block text-sm font-medium leading-6 text-left text-gray-900">Nationalité :</label>
            <CountrySelect @update:country="setCountry"/>
            <p v-if="v$.$errors.find(err => err.$property === 'nationalityCountryCode')"
               class="mt-1 text-sm text-left text-red-500">{{
                v$.$errors.find(err => err.$property === 'nationalityCountryCode')?.$message
              }}</p>
          </div>
          <div class="mt-2">
            <BaseInput v-model="form.ticketNumber" :value="form.ticketNumber" label="Numéro de billet" :icon="TicketIcon" placeholder="057-9389633557" /> 
            <p v-if="v$.$errors.find(err => err.$property === 'ticketNumber')"
               class="mt-1 text-sm text-left text-red-500">{{
                v$.$errors.find(err => err.$property === 'ticketNumber')?.$message
              }}</p>
          </div>
        </div>
        <div class="mt-6">
          <BaseButton @click="addPassenger" class="h-9" text="Enregistrer le passager"
                      :disabled="v$.$errors.length > 0"
                      type="submit"
                      size="md"/>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 px-4 pb-6 ">
        <PassengerCard v-for="(pax, index) in passengers" :key="index" :passenger="pax"
                       @remove-passenger="onRemovePassenger(index)"/>
      </div>
    </form>
  </div>

</template>